package com.starparty.app.astro0.js

import com.starparty.app.astro0.SP_Time
import com.starparty.app.astro0.Sky
import com.starparty.app.astro0.UMS
import dateTime
import initialiseDateTime
import kotlinx.browser.document
import org.w3c.dom.*
import kotlin.js.Date
import kotlin.math.abs
import kotlin.math.absoluteValue

class Controls(val sky:Sky,
               val cs:CanvasState) {

    val root = document.getElementById("sp-root") as HTMLDivElement

    val locationFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val locationLegend = document.createElement("legend") as HTMLLegendElement
    val latDegSelect = document.createElement("select") as HTMLSelectElement
    val latMinSelect = document.createElement("select") as HTMLSelectElement
    val latNSSelect = document.createElement("select") as HTMLSelectElement
    val lonDegSelect = document.createElement("select") as HTMLSelectElement
    val lonMinSelect = document.createElement("select") as HTMLSelectElement
    val lonEWSelect = document.createElement("select") as HTMLSelectElement

    val dateFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val dateLegend = document.createElement("legend") as HTMLLegendElement
    val timeFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val timeLegend = document.createElement("legend") as HTMLLegendElement
    val daySelect = document.createElement("select") as HTMLSelectElement
    val monthSelect = document.createElement("select") as HTMLSelectElement
    val yearSelect = document.createElement("select") as HTMLSelectElement
    val hourSelect = document.createElement("select") as HTMLSelectElement
    val minSelect = document.createElement("select") as HTMLSelectElement

    val timeZoneFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val timeZoneLegend = document.createElement("legend") as HTMLLegendElement
    val timeZoneHoursSelect = document.createElement("select") as HTMLSelectElement
    val timeZoneMinsSelect = document.createElement("select") as HTMLSelectElement

    val nowButton = document.createElement("button") as HTMLButtonElement

    val zoomFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val zoomLegend = document.createElement("legend") as HTMLLegendElement
    val zoomInButton = document.createElement("button") as HTMLButtonElement
    val zoomOutButton = document.createElement("button") as HTMLButtonElement

    val directionFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val directionLegend = document.createElement("legend") as HTMLLegendElement
    val northButton = document.createElement("button") as HTMLButtonElement
    val southButton = document.createElement("button") as HTMLButtonElement
    val eastButton = document.createElement("button") as HTMLButtonElement
    val westButton = document.createElement("button") as HTMLButtonElement

    val rotateFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val rotateLegend = document.createElement("legend") as HTMLLegendElement
    val rotateClockwiseButton = document.createElement("button") as HTMLButtonElement
    val rotateAnticlockwiseButton = document.createElement("button") as HTMLButtonElement

    val showFieldSet = document.createElement("fieldset") as HTMLFieldSetElement
    val showLegend = document.createElement("legend") as HTMLLegendElement
    val equGridButton = document.createElement("button") as HTMLButtonElement
    val altazGridButton = document.createElement("button") as HTMLButtonElement

    val showStarsButton = document.createElement("button") as HTMLButtonElement
    val showConstellationsButton = document.createElement("button") as HTMLButtonElement
    val showPlanetsButton = document.createElement("button") as HTMLButtonElement
    val showDSOsButton = document.createElement("button") as HTMLButtonElement
    val showLabelsButton = document.createElement("button") as HTMLButtonElement

    companion object {
        var zoomInc = 0.05
        var rotInc = 5.0
    }

    fun setupZoomButtons() {

        zoomInButton.addEventListener("click", { zoomChanged(zoomInc) })
        zoomOutButton.addEventListener("click", { zoomChanged(-zoomInc) })

        zoomInButton.innerText = "in"
        zoomOutButton.innerText = "out"

        zoomInButton.title = "Zoom In"
        zoomOutButton.title = "Zoom Out"

        zoomFieldSet.id="sp-zoom-set"
        zoomLegend.innerText = "Zoom:"
        zoomFieldSet.appendChild(zoomLegend)
        zoomFieldSet.appendChild(zoomInButton)
        zoomFieldSet.appendChild(zoomOutButton)
        root.appendChild(zoomFieldSet)
    }

    fun setupRotateButtons() {

        rotateClockwiseButton.addEventListener("click", { rotationChanged(-rotInc) })
        rotateAnticlockwiseButton.addEventListener("click", { rotationChanged(rotInc) })

        rotateClockwiseButton.innerText = "\u21BB"
        rotateAnticlockwiseButton.innerText = "\u21BA"

        rotateClockwiseButton.title = "Rotate Clockwise"
        rotateAnticlockwiseButton.title = "Rotate Anticlockwise"

        rotateFieldSet.id="sp-rotation-set"
        rotateLegend.innerText = "Rotate:"
        rotateFieldSet.appendChild(rotateLegend)
        rotateFieldSet.appendChild(rotateClockwiseButton)
        rotateFieldSet.appendChild(rotateAnticlockwiseButton)
        root.appendChild(rotateFieldSet)
    }

    fun setupDirectionButtons() {

        northButton.addEventListener("click", { directionChanged(0.0) })
        eastButton.addEventListener("click", { directionChanged(90.0) })
        southButton.addEventListener("click", { directionChanged(180.0) })
        westButton.addEventListener("click", { directionChanged(270.0) })

        northButton.innerText = "N"
        eastButton.innerText  = "E"
        southButton.innerText = "S"
        westButton.innerText  = "W"

        northButton.title = "Face North"
        eastButton.title  = "Face East"
        southButton.title = "Face South"
        westButton.title  = "Face West"

        directionFieldSet.id="direction"
        directionLegend.innerText = "Direction:"
        directionFieldSet.appendChild(directionLegend)
        directionFieldSet.appendChild(northButton)
        directionFieldSet.appendChild(eastButton)
        directionFieldSet.appendChild(southButton)
        directionFieldSet.appendChild(westButton)
        root.appendChild(directionFieldSet)
    }

    fun setupShowButtons() {

        equGridButton.addEventListener("click", { equGridChanged()  })
        altazGridButton.addEventListener("click", { altazGridChanged() })

        equGridButton.innerText = "Eq"
        altazGridButton.innerText  = "AA"

        equGridButton.title  = "Switch Equatorial Grid Type"
        altazGridButton.title  = "Switch Horizontal (Altitude/Azimuth) Grid Type"

        showFieldSet.id="show"
        showLegend.innerText = "Show:"
        showFieldSet.appendChild(showLegend)
        showFieldSet.appendChild(equGridButton)
        showFieldSet.appendChild(altazGridButton)

        showStarsButton.addEventListener("click", { showStarsChanged() })
        showPlanetsButton.addEventListener("click", { showPlanetsChanged() })
        showDSOsButton.addEventListener("click", { showDSOsChanged() })
        showConstellationsButton.addEventListener("click", { showConstellationsChanged() })
        showLabelsButton.addEventListener("click", { showLabelsChanged() })

        showStarsButton.innerText = "\u2605"
        showDSOsButton.innerText  = "M"
        showPlanetsButton.innerText = "Planets"
        showConstellationsButton.innerText  = "Const"
        showLabelsButton.innerText = "Labels"

        showStarsButton.title = "Show/Hide Stars"
        showDSOsButton.title  = "Show/Hide Messier Objects"
        showPlanetsButton.title = "Show/Hide Planets, Sun and Moon"
        showConstellationsButton.title  = "Show/Hide Constellations"
        showLabelsButton.title = "Show object names - Full/Abbreviated/Off"

        showFieldSet.appendChild(showStarsButton)
        showFieldSet.appendChild(showDSOsButton)
        showFieldSet.appendChild(showPlanetsButton)
        showFieldSet.appendChild(showConstellationsButton)
        showFieldSet.appendChild(showLabelsButton)

        root.appendChild(showFieldSet)
    }

    fun showLabelsChanged() {

        sky.showLabels++

        if (sky.showLabels > Sky.ABBR_TITLES) {
                sky.showLabels = Sky.NO_TITLES
        }

        sky.project()
        cs.changed = true
        cs.draw()
    }

    fun rotationChanged(rotInc: Double) {

        sky.roll(rotInc)
        sky.project()
        cs.changed = true
        cs.draw()
    }

    fun zoomChanged(zoomInc: Double) {

        sky.incrementZoom(1 + zoomInc)
        sky.project()
        cs.changed = true
        cs.draw()
    }

    fun equGridChanged(){
        sky.rotateEquatorialGridType()
        cs.changed = true
        cs.draw()
    }

    fun altazGridChanged(){
        sky.rotateAltAzmGridType()
        cs.changed = true
        cs.draw()
    }

    fun showStarsChanged(){
        sky.starField.isShowingStars = ! sky.starField.isShowingStars
        cs.changed = true
        cs.draw()
    }

    fun showPlanetsChanged(){
        sky.orrery.isShowingObjects = ! sky.orrery.isShowingObjects
        cs.changed = true
        cs.draw()
    }

    fun showDSOsChanged(){
        sky.deepSkyField.isShowingDSOs = ! sky.deepSkyField.isShowingDSOs
        cs.changed = true
        cs.draw()
    }

    fun showConstellationsChanged(){
        sky.starField.isShowingConstellations = ! sky.starField.isShowingConstellations
        cs.changed = true
        cs.draw()
    }


    fun directionChanged(direction: Double) {

        sky.setAltAzmCoordinatesWithZenithUp(direction, 0.0)

        sky.project()
        cs.changed = true
        cs.draw()
    }

    fun setTimeToNow() {
        dateTime = initialiseDateTime()
        sky.setTime(dateTime)
        syncDateTimeSpinners()
    }

    fun setupDateTimeControls(timeZoneOffsetHours: Int, timeZoneOffsetMins: Int) {

        val tzoHours = timeZoneOffsetHours.toString()
        val tzoMins = timeZoneOffsetMins.toString()
        val curTime = sky.getTime()

        val year = curTime.yearUTC()
        val earliestYear = year - 20
        val latestYear = year + 20

        addTimeZoneOptions()
        timeZoneHoursSelect.value = tzoHours
        timeZoneMinsSelect.value = tzoMins

        addNumberedOptions(daySelect, 1, 31, "", 0, true)
        addMonthOptions(monthSelect)
        addNumberedOptions(yearSelect, earliestYear, latestYear, "", 0, false)

        addNumberedOptions(hourSelect, 0, 23, "", 0, true)
        addNumberedOptions(minSelect, 0, 59, "", 0, true)

        daySelect.addEventListener("change", { timeSpinnerChanged() })
        monthSelect.addEventListener("change", { timeSpinnerChanged() })
        yearSelect.addEventListener("change", { timeSpinnerChanged() })

        hourSelect.addEventListener("change", { timeSpinnerChanged() })
        minSelect.addEventListener("change", { timeSpinnerChanged() })

        timeZoneHoursSelect.addEventListener("change", { syncDateTimeSpinners() })
        timeZoneMinsSelect.addEventListener("change", { syncDateTimeSpinners() })

        nowButton.addEventListener("click", { setTimeToNow() })

        setTimeToNow()

        dateFieldSet.id = "sp-date-set"
        dateFieldSet.appendChild(dateLegend)
        dateLegend.innerText = "Date:"
        dateFieldSet.appendChild(daySelect)
        dateFieldSet.appendChild(monthSelect)
        dateFieldSet.appendChild(yearSelect)

        timeFieldSet.id = "sp-time-set"
        timeFieldSet.appendChild(timeLegend)
        timeLegend.innerText = "Time:"
        timeFieldSet.appendChild(hourSelect)
        timeFieldSet.appendChild(minSelect)

        nowButton.textContent = "Now"
        nowButton.title = "Set to Current Date and Time"

        timeFieldSet.appendChild(nowButton)

        timeZoneLegend.innerText = "Time Zone:"
        timeZoneFieldSet.appendChild(timeZoneHoursSelect)
        timeZoneFieldSet.appendChild(timeZoneMinsSelect)
        timeZoneFieldSet.appendChild(timeZoneLegend)

        root.appendChild(dateFieldSet)
        root.appendChild(timeFieldSet)
        root.appendChild(timeZoneFieldSet)
    }

    fun setupLocationSpinners() {

        val curPlace = sky.getPlace()
        val dmsLat = UMS.degreesToDMS(curPlace.latitude)
        val dmsLon = UMS.degreesToDMS(curPlace.longitude)
        val latDeg = abs(dmsLat.units.toInt())
        val latMin = dmsLat.mins.toInt()
        val lonDeg = abs(dmsLon.units.toInt())
        val lonMin = dmsLon.mins.toInt()

        addNumberedOptions(latDegSelect, 0, 90, "°", latDeg, false)
        addNumberedOptions(latMinSelect, 0, 59, "m", latMin, true)

        addNumberedOptions(lonDegSelect, 0, 180, "°", lonDeg, false)
        addNumberedOptions(lonMinSelect, 0, 59, "m", lonMin, true)
        // addNumberedOptions("sp-lon-sec",1, 59, "s")

        addNSEWOptions()

        if (curPlace.latitude < 0) {
            latNSSelect.selectedIndex = 1
        }
        if (curPlace.longitude < 0) {
            lonEWSelect.selectedIndex = 1
        }

        latDegSelect.addEventListener("change", { locationChanged() })
        latMinSelect.addEventListener("change", { locationChanged() })
        latNSSelect.addEventListener("change", { locationChanged() })

        lonDegSelect.addEventListener("change", { locationChanged() })
        lonMinSelect.addEventListener("change", { locationChanged() })
        lonEWSelect.addEventListener("change", { locationChanged() })

        locationFieldSet.id = "sp-loc-set"
        locationFieldSet.appendChild(locationLegend)
        locationLegend.innerText = "Location:"
        locationFieldSet.appendChild(latDegSelect)
        locationFieldSet.appendChild(latMinSelect)
        locationFieldSet.appendChild(latNSSelect)
        locationFieldSet.appendChild(lonDegSelect)
        locationFieldSet.appendChild(lonMinSelect)
        locationFieldSet.appendChild(lonEWSelect)
        root.appendChild(locationFieldSet)
    }


    fun addNumberedOptions(
        select: HTMLSelectElement,
        startNum: Int,
        finishNum: Int,
        unit: String,
        selectedOption: Int,
        leadingZeros: Boolean
    ) {

        for (i in startNum..finishNum) {
            val sel = (i == selectedOption)
            var text = "$i$unit"

            if (leadingZeros && (i in 0..9)) {
                text = "0$text"
            }
            val option = Option(text, i.toString(), false, sel)
            select.options.add(option)
        }
    }

    fun addTimeZoneOptions()
    {
        /*
        val options = arrayOf(
            Option("UTC+14","14"),
            Option("UTC+13","13"),
            Option("UTC+12:45", "12.75"),
            Option("UTC+12", "12"),
            Option("UTC+11", "11"),
            Option("UTC+10:30", "10.5"),
            Option("UTC+10", "10"),
            Option("UTC+9:30", "9.5"),
            Option("UTC+9", "9"),
            Option("UTC+8", "8"),
            Option("UTC+7", "7"),
            Option("UTC+6:30", "6.5"),
            Option("UTC+6", "6"),
            Option("UTC+5:45", "5.75"),
            Option("UTC+5:30", "5.5"),
            Option("UTC+5", "5"),
            Option("UTC+4:30", "4.5"),
            Option("UTC+4", "4"),
            Option("UTC+3", "3"),
            Option("UTC+2", "2"),
            Option("UTC+1", "1"),
            Option("UTC+0", "0", true, true),
            Option("UTC-1", "-1"),
            Option("UTC-2", "-2"),
            Option("UTC-2:30", "-2.5"),
            Option("UTC-3", "-3"),
            Option("UTC-4", "-4"),
            Option("UTC-5", "-5"),
            Option("UTC-6", "-6"),
            Option("UTC-7", "-7"),
            Option("UTC-8", "-8"),
            Option("UTC-9", "-9"),
            Option("UTC-9:30", "-9.5"),
            Option("UTC-10", "-10"),
            Option("UTC-11", "-11"),
            Option("UTC-12", "-12")
        ) */

        for (i in -14..14) {
            val sign = if (i >= 0) "+" else ""
            val option = Option("UTC$sign$i hrs", i.toString())
           timeZoneHoursSelect.options.add(option)
        }

        timeZoneMinsSelect.options.add(Option("00 mins", "0"))
        timeZoneMinsSelect.options.add(Option("15 mins", "15"))
        timeZoneMinsSelect.options.add(Option("30 mins", "30"))
        timeZoneMinsSelect.options.add(Option("45 mins", "45"))
    }

    fun addNSEWOptions() {

        val optionE = Option("E", "E")
        val optionW = Option("W", "W")
        val optionN = Option("N", "N")
        val optionS = Option("S", "S")
        lonEWSelect.options.add(optionE)
        lonEWSelect.options.add(optionW)
        latNSSelect.options.add(optionN)
        latNSSelect.options.add(optionS)
    }

    fun addMonthOptions(select: HTMLSelectElement) {

        val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        for (i in 0..11) {
            val option = Option(months[i], i.toString())
            select.options.add(option)
        }
    }

    fun syncDateTimeSpinners() {

        val curTime = sky.getTime()

        var day = curTime.dayOfMonthUTC()
        val month = curTime.monthUTC() - 1
        val year = curTime.yearUTC()

        var hour = curTime.hourOfDayUTC()
        val min = curTime.minuteUTC()
        val sec = curTime.secondUTC()

        val tzoHrs = timeZoneHoursSelect.value.toDouble()
        val tzoSign = if (tzoHrs < 0) -1 else 1
        val tzoMins = timeZoneMinsSelect.value.toDouble()
        var timeZoneSecondsOffset = tzoSign * ((tzoHrs.absoluteValue * 60 * 60)  + (tzoMins * 60))

        val jsDT_zoned = Date(year, month, day, hour, min, sec+timeZoneSecondsOffset.toInt())

        yearSelect.value = jsDT_zoned.getFullYear().toString()
        monthSelect.selectedIndex = jsDT_zoned.getMonth()
        daySelect.selectedIndex = jsDT_zoned.getDate() - 1
        hourSelect.selectedIndex = jsDT_zoned.getHours()
        minSelect.selectedIndex = jsDT_zoned.getMinutes()

       // timeZoneHoursSelect.selectedIndex = tzoHrs.toInt()
       // timeZoneMinsSelect.selectedIndex = tzoMins.toInt()

        sky.project()
        cs.changed = true
        cs.draw()
    }


    fun timeSpinnerChanged() {
/*
        val daySelect = document.getElementById("sp-time-date") as HTMLSelectElement
        val monthSelect = document.getElementById("sp-time-mon") as HTMLSelectElement
        val yearSelect = document.getElementById("sp-time-year") as HTMLSelectElement

        val hourSelect = document.getElementById("sp-time-hour") as HTMLSelectElement
        val minSelect = document.getElementById("sp-time-min") as HTMLSelectElement
        //  val secSelect = document.getElementById("sp-time-sec") as HTMLSelectElement

        val timeZoneSelect = document.getElementById("sp-time-zone") as HTMLSelectElement
*/
        var day = daySelect.value.toInt()
        val month = monthSelect.value.toInt()
        val year = yearSelect.value.toInt()

        var hour = hourSelect.value.toInt()
        val min = minSelect.value.toInt()
        // val sec = secSelect.value.toInt()
 //       console.log("hour $hour, min $min", timeZoneSelect.value)
        val tzoHours = timeZoneHoursSelect.value.toDouble() * 60 * 60
        val tzoMins = timeZoneMinsSelect.value.toDouble() * 60
        val tzoSign = if (tzoHours < 0) 1 else -1
        val timeZoneSecondsOffset = tzoSign * (tzoHours + tzoMins)

        val jsDT_zoned = Date(year, month, day, hour, min)

        val jsDT_UTC = Date(year, month, day, hour, min, timeZoneSecondsOffset.toInt())

        val utcYear = jsDT_UTC.getFullYear()
        val utcMonth = jsDT_UTC.getMonth()
        val utcDay = jsDT_UTC.getDate()
        val utcHour = jsDT_UTC.getHours()
        val utcMin = jsDT_UTC.getMinutes()

        val spTime = SP_Time(utcYear, utcMonth + 1, utcDay, utcHour, utcMin, 0)
 //console.log("${jsDT_zoned.getFullYear()}, ${jsDT_zoned.getMonth()}, ${jsDT_zoned.getDate()}, ${jsDT_zoned.getHours()}, ${jsDT_zoned.getMinutes()}, ${jsDT_zoned.getSeconds()}")
 //console.log("UTC: ${spTime.year}, ${spTime.month}, ${spTime.dayOfMonth}, ${spTime.hour}, ${spTime.minute}, ${spTime.second}")

        yearSelect.value = jsDT_zoned.getFullYear().toString()
        monthSelect.selectedIndex = jsDT_zoned.getMonth()
        daySelect.selectedIndex = jsDT_zoned.getDate() - 1
        hourSelect.selectedIndex = jsDT_zoned.getHours()
        minSelect.selectedIndex = jsDT_zoned.getMinutes()

        sky.setTime(spTime)
        sky.project()
        cs.changed = true
        cs.draw()
    }


    fun locationChanged() {
/*
        val latDegSelect = document.getElementById("sp-lat-deg") as HTMLSelectElement
        val latMinSelect = document.getElementById("sp-lat-min") as HTMLSelectElement
        val latNSSelect = document.getElementById("sp-lat-NS") as HTMLSelectElement

        val lonDegSelect = document.getElementById("sp-lon-deg") as HTMLSelectElement
        val lonMinSelect = document.getElementById("sp-lon-min") as HTMLSelectElement
        val lonEWSelect = document.getElementById("sp-lon-EW") as HTMLSelectElement
*/

        val latDeg = latDegSelect.value.toDouble()
        val latMin = latMinSelect.value.toDouble()
        val latNS = latNSSelect.value

        val lonDeg = lonDegSelect.value.toDouble()
        val lonMin = lonMinSelect.value.toDouble()
        val lonEW = lonEWSelect.value

        var latSign = 1
        var lonSign = 1
        if ("S" == latNS) { latSign = -1 }
        if ("W" == lonEW) { lonSign = -1 }

        val lat = UMS.unitMinSecToDecimal(latSign, latDeg, latMin, 0.0)
        val lon = UMS.unitMinSecToDecimal(lonSign, lonDeg, lonMin, 0.0)

        sky.setLat(lat)
        sky.setLon(lon)

        sky.setAltAzmCoordinatesWithZenithUp(0.0, 0.0)

        sky.project()
        cs.changed = true
        cs.draw()
    }

    fun setupCanvas() {
        root.appendChild(cs.spCanvas.canvas)
    }

}